import type {
  CreateSlotDTO,
  Slot,
  SlotRankCheckStatus,
} from '../Modules/Slot/Model/Slot';

const RANK_TRIGGER_KEYS = [
  'main_keyword',
  'product_url',
  'compare_url',
  'mid_single',
  'mid_compare',
] as const;

/** 키워드 + 상품/비교 URL이 있으면 서버가 MID·순위를 백그라운드로 계산 */
export function expectsAsyncRank(
  slot: Pick<Slot, 'main_keyword' | 'product_url' | 'compare_url'> | CreateSlotDTO | Partial<Slot>,
): boolean {
  const keyword = String(slot.main_keyword ?? '').trim();
  const productUrl = String(slot.product_url ?? '').trim();
  const compareUrl = String(slot.compare_url ?? '').trim();
  return Boolean(keyword && (productUrl || compareUrl));
}

/** 수정 DTO에 순위 재계산 트리거 필드가 포함됐는지 */
export function dtoTriggersRankRecalc(dto: Partial<Slot>): boolean {
  return RANK_TRIGGER_KEYS.some((key) => {
    const value = dto[key];
    return value !== undefined && value !== null;
  });
}

/**
 * 서버의 rank_check_status가 있으면 그것을 우선 신뢰한다.
 * 이전 서버 응답과의 호환을 위해 상태가 없을 때만 rank_history를 사용한다.
 */
export function isRankCalculationComplete(
  slot: Pick<Slot, 'rank_history' | 'rank_check_status'>,
): boolean {
  const rankStatus = slot.rank_check_status;
  if (rankStatus) return rankStatus === 'complete' || rankStatus === 'failed';

  const { rank_history: history } = slot;
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  if (Array.isArray(history)) {
    return history.some((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const value = entry as { date?: unknown; checked_at?: unknown };
      const date = typeof value.date === 'string'
        ? value.date
        : typeof value.checked_at === 'string'
          ? value.checked_at.slice(0, 10)
          : '';
      return date === today;
    });
  }

  return Boolean(history && Object.prototype.hasOwnProperty.call(history, today));
}

export function isRankCheckInProgress(
  status: SlotRankCheckStatus | undefined,
): boolean {
  return status === 'pending' || status === 'running';
}

export const RANK_POLL_INTERVAL_MS = 15_000;
export const RANK_POLL_MAX_MS = 120_000;
export const RANK_POLL_DELAYED_INTERVAL_MS = 30_000;
